import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo
} from 'react';
import { useLocation } from 'react-router-dom';
import { useOrderVisit } from '@/hooks/useOrderVisit';
import { getDetailOrderId } from '@/utils/getDetailOrderId';
import { getOrderId, isMobile } from '@/biz/tool';
import { shouldRequireCreatorAgreement } from '@/biz/creatorAgreement';
import toast from 'react-hot-toast';
import WebModal from '../Modal';
import BottomDrawer from '../BottomDrawer';
import type { StripeAddressElement } from '@stripe/stripe-js';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import { useTranslation } from 'react-i18next';
import { containsProhibitedPOBox, validateRFC } from './utils';
import SelectProductContent from './SelectProductContent';
import ModalContent from './ModalContent';
import AgreementContent from './AgreementContent';
import { creatorOrderV2Api } from '@/apis/creatorOrderV2/creatorOrderV2Api';
import { isCreatorOrderV2ApiError } from '@/apis/creatorOrderV2/errors';
import {
  assertCreatorDeliveryAction,
  handleCreatorDeliveryWriteError
} from '@/biz/multiPlatformCollaboration/creatorDeliveryActions';
import { v4 as uuidv4 } from 'uuid';
import { useCreatorDeliveryTimelineStore } from '@/hooks/useCreatorDeliveryTimelineStore';
import { getCreatorTimelineContentNodes } from '@/biz/multiPlatformCollaboration/creatorDeliveryTimeline';
import { ActionsStatusEnum } from '../../home/config';
import { showCreatorDeliveryCompletion } from '@/hooks/creatorDeliveryCompletion';

import {
  createProductSelectionDraft,
  normalizePhysicalSamplingConfig,
  normalizeProductSelectionSnapshot,
  ProductOptionRequirement,
  ProductSelectionDraft,
  ProductSelectionSubmit,
  SamplingMode
} from '../PhysicalProductSelection';

interface Props {
  submitProductSelection?: ProductSelectionSubmit;
}

type AddressFlowStep = 'agreement' | 'product' | 'address';

const ModalAddress: React.FC<Props & { orderId: string }> = ({
  orderId,
  submitProductSelection
}) => {
  const { t } = useTranslation();
  const { detailData, reQueryDetailData } = useDetailDataStore();
  const { isShowAddressModal, setShowAddressModal, isAgree, setIsAgree } =
    useWebModalStore();
  const [complete, setComplete] = useState(false);
  const requestRef = useRef({ pending: false, fingerprint: '', key: '' });
  const modalVisit = useRef(0);
  useLayoutEffect(() => {
    modalVisit.current += 1;
  }, [isShowAddressModal]);
  const [stripeComplete, setStripeComplete] = useState(false);
  const [poBoxInvalid, setPoBoxInvalid] = useState(false);
  const [address, setAddress] = useState<any>(null);
  const addressElementRef = useRef<StripeAddressElement | null>(null);
  const showRfcValidationRef = useRef<(() => void) | null>(null);
  const isCurrentOrderVisit = useOrderVisit(orderId);
  const { product, order } = detailData;
  const shouldShowCreatorAgreement = shouldRequireCreatorAgreement(
    detailData?.order?.appliedAt
  );
  const [creatorAgreementError, setCreatorAgreementError] = useState(false);
  const [addressFlowStep, setAddressFlowStep] =
    useState<AddressFlowStep>('agreement');
  const [agreementChecked, setAgreementChecked] = useState<[boolean, boolean]>([
    false,
    false
  ]);
  const [addAddressToAccount, setAddAddressToAccount] = useState(false);
  const [guidePreviewVisible, setGuidePreviewVisible] = useState(false);
  const samplingConfig = useMemo(
    () => normalizePhysicalSamplingConfig(product),
    [product]
  );
  const selectionSnapshot = useMemo(
    () => normalizeProductSelectionSnapshot(order, samplingConfig),
    [order, samplingConfig]
  );
  const hasProductSelectionStep = Boolean(
    samplingConfig &&
      (typeof product?.physicalProductSelectionRequired === 'boolean'
        ? product.physicalProductSelectionRequired
        : samplingConfig.mode === SamplingMode.MULTIPLE_PRODUCTS ||
          samplingConfig.products[0]?.optionRequirement ===
            ProductOptionRequirement.REQUIRED)
  );
  const [flowHasProductSelection, setFlowHasProductSelection] = useState(
    hasProductSelectionStep
  );
  const [selectionDraft, setSelectionDraft] = useState<ProductSelectionDraft>(
    () =>
      samplingConfig
        ? createProductSelectionDraft(samplingConfig, selectionSnapshot)
        : { selectedProductIds: [], valuesByProductId: {} }
  );

  useEffect(() => {
    if (!isShowAddressModal) return;
    setAddressFlowStep('agreement');
    setGuidePreviewVisible(false);
    setAgreementChecked([false, false]);
    setIsAgree(false);
    setCreatorAgreementError(false);
    setFlowHasProductSelection(hasProductSelectionStep);
    setSelectionDraft(
      samplingConfig
        ? createProductSelectionDraft(samplingConfig, selectionSnapshot)
        : { selectedProductIds: [], valuesByProductId: {} }
    );
    // The flow must stay stable while product submission refreshes order data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowAddressModal]);

  const handleSubmit = async () => {
    if (!isCurrentOrderVisit() || getDetailOrderId(order) !== orderId) return;
    const visit = modalVisit.current;
    const isCurrentSubmission = () =>
      isCurrentOrderVisit() && modalVisit.current === visit;
    const request = requestRef.current;
    if (request.pending) return;
    if (!complete) {
      // Stripe AddressElement: getValue() validates and shows errors under each field
      await addressElementRef.current?.getValue();
      showRfcValidationRef.current?.();
      return;
    }
    if (shouldShowCreatorAgreement && !isAgree) {
      setCreatorAgreementError(true);
      return;
    }
    try {
      request.pending = true;
      const addressText = `${(address as any)?.line1 || ''} ${(address as any)?.line2 || ''}`;
      if (containsProhibitedPOBox(addressText)) {
        toast.error(
          t('modal.address.no_po_box.error', {
            defaultValue:
              'Please provide a physical shipping address (no PO Boxes).'
          })
        );
        return;
      }
      assertCreatorDeliveryAction(orderId, 'SUBMIT_ADDRESS');
      const timelineState = useCreatorDeliveryTimelineStore.getState();
      const addressNode = getCreatorTimelineContentNodes(
        timelineState.timeline
      ).find(
        (node) =>
          node.code === 'ADDRESS' && node.actions.includes('SUBMIT_ADDRESS')
      );
      const body = { addressInfo: address, saveToAccount: addAddressToAccount };
      const fingerprint = JSON.stringify({ orderId, body });
      if (request.fingerprint !== fingerprint) {
        request.fingerprint = fingerprint;
        request.key = uuidv4();
      }
      await creatorOrderV2Api.address(orderId, body, request.key);
      if (!isCurrentSubmission()) return;
      // Snapshot the completed address stage before refresh advances the timeline.
      const now =
        Date.parse(timelineState.timeline?.serverNow || '') +
        Math.max(0, Date.now() - timelineState.receivedAt);
      const dueAt = addressNode?.deadline?.dueAt;
      const bonus =
        detailData?.order?.interestedPriceFields?.[0]?.bonusAmount > 0 &&
        (addressNode?.data.appeal?.status === 'approved' ||
          Boolean(dueAt && Date.parse(dueAt) > now));
      await reQueryDetailData({ orderId });
      if (!isCurrentSubmission()) return;
      setShowAddressModal(false);
      request.fingerprint = '';
      showCreatorDeliveryCompletion({
        orderId,
        node: ActionsStatusEnum.PendingCreatorAddress,
        mode: bonus ? 'bonus' : 'node',
        isCurrentVisit: isCurrentOrderVisit,
        refresh: () => reQueryDetailData({ orderId })
      });
    } catch (error: unknown) {
      if (!isCurrentSubmission()) return;
      handleCreatorDeliveryWriteError(error);
      if (isCreatorOrderV2ApiError(error)) request.fingerprint = '';
      if (
        isCreatorOrderV2ApiError(error) &&
        (error.recoveryAction === 'REFRESH_WITHOUT_REPLAY' ||
          error.recoveryAction === 'REFRESH_DELIVERY_REVISION' ||
          error.code === 47002)
      ) {
        await reQueryDetailData({ orderId });
        if (!isCurrentSubmission()) return;
        toast.error(
          t('multi_platform.delivery.state_conflict', {
            defaultValue:
              'The collaboration status changed. The latest details have been loaded and nothing was resubmitted.'
          })
        );
        return;
      }
      toast.error(
        (error instanceof Error ? error.message : '') ||
          t('modal.address.submit.error', {
            defaultValue: 'Submission failed.'
          })
      );
    } finally {
      request.pending = false;
    }
  };

  const handleAgreementContinue = () => {
    setAddressFlowStep(flowHasProductSelection ? 'product' : 'address');
  };

  const handleAddressChange = (event: any) => {
    const { complete, value, rfcValue } = event;

    const { address, name, phone } = value;
    setAddress({
      ...address,
      name,
      phone,
      rfcValue: address?.country === 'MX' ? rfcValue : ''
    });
    setStripeComplete(complete);

    const addressText = `${(address as any)?.line1 || ''} ${(address as any)?.line2 || ''}`;
    const hasProhibitedPOBox = containsProhibitedPOBox(addressText);
    if (hasProhibitedPOBox) {
      // 每次检测到禁止内容都提示，避免从一种禁止写法改成另一种时用户看不到报错
      toast.error(
        t('modal.address.no_po_box.error', {
          defaultValue:
            'Please provide a physical shipping address (no PO Boxes).'
        })
      );
      setPoBoxInvalid(true);
      setComplete(false);
      return;
    }
    if (poBoxInvalid) {
      setPoBoxInvalid(false);
    }

    if (address?.country === 'MX' && !validateRFC(rfcValue)) {
      setComplete(false);
      return;
    }
    setComplete(complete);
  };

  const addressFlowContent =
    addressFlowStep === 'agreement' ? (
      <AgreementContent
        checked={agreementChecked}
        onCheckedChange={setAgreementChecked}
        onCancel={() => setShowAddressModal(false)}
        onContinue={handleAgreementContinue}
        hasProductSelection={flowHasProductSelection}
        shouldShowCreatorAgreement={shouldShowCreatorAgreement}
        creatorAgreementError={creatorAgreementError}
        setCreatorAgreementError={setCreatorAgreementError}
      />
    ) : addressFlowStep === 'product' ? (
      samplingConfig ? (
        <SelectProductContent
          autoFocus={isShowAddressModal}
          config={samplingConfig}
          draft={selectionDraft}
          setDraft={setSelectionDraft}
          setShowAddressModal={setShowAddressModal}
          onContinue={() => setAddressFlowStep('address')}
          onBack={() => setAddressFlowStep('agreement')}
          submitSelection={submitProductSelection}
          onGuidePreviewVisibleChange={setGuidePreviewVisible}
        />
      ) : null
    ) : (
      <ModalContent
        autoFocus={isShowAddressModal}
        handleAddressChange={handleAddressChange}
        addressInfo={address}
        complete={stripeComplete}
        addressElementRef={addressElementRef}
        showRfcValidationRef={showRfcValidationRef}
        addAddressToAccount={addAddressToAccount}
        setAddAddressToAccount={setAddAddressToAccount}
        hasProductSelection={flowHasProductSelection}
      />
    );

  return (
    <>
      {isMobile() ? (
        <BottomDrawer
          disableEnforceFocus={guidePreviewVisible}
          disableEscapeKeyDown={guidePreviewVisible}
          visible={isShowAddressModal}
          handleClose={() => setShowAddressModal(false)}
          handleSubmit={handleSubmit}
          isShowHeader={false}
          isShowFooter={addressFlowStep === 'address'}
          isShowBack={flowHasProductSelection}
          handleBack={() => setAddressFlowStep('product')}
          okText={t('modal.address.confirm', { defaultValue: 'Submit' })}
        >
          {addressFlowContent}
        </BottomDrawer>
      ) : (
        <WebModal
          manageFocus
          disableEnforceFocus={guidePreviewVisible}
          disableEscapeKeyDown={guidePreviewVisible}
          canClose
          ariaLabel={t('modal.address.select_product_title', {
            defaultValue: 'Select your product'
          })}
          width={560}
          style={{ maxHeight: 'calc(100dvh - 48px)' }}
          visible={isShowAddressModal}
          handleClose={() => setShowAddressModal(false)}
          handleSubmit={handleSubmit}
          isShowHeader={false}
          isShowFooter={addressFlowStep === 'address'}
          okText={t('modal.address.confirm', { defaultValue: 'Submit' })}
          isShowBack={flowHasProductSelection}
          handleBack={() => setAddressFlowStep('product')}
        >
          {addressFlowContent}
        </WebModal>
      )}
    </>
  );
};

// The modal is global; remount its form when the route changes so addresses and
// validation from one order cannot be submitted for another.
export default function OrderAddressModal({
  submitProductSelection
}: Props = {}) {
  const location = useLocation();
  const orderId = getOrderId();
  useLayoutEffect(
    () => () => {
      useWebModalStore.getState().setShowAddressModal(false);
    },
    [location.pathname]
  );
  return (
    <ModalAddress
      key={orderId}
      orderId={orderId}
      submitProductSelection={submitProductSelection}
    />
  );
}
